










































































import storeHelper from "@/store/store-helper";
import { AiCacheSearchResultItem } from "@/types/ai/aiSearch";
import { defineComponent, ref, computed, watch, getCurrentInstance, onMounted, onBeforeMount } from "@vue/composition-api";
import AiFeedbackNotFound from "@/components/ai-results/AiFeedbackNotFound.vue";
import SectionHeader from "@/components/generic/SectionHeader.vue";
import { ContentItem } from "@infoland/iprova-api";
import Utils from "@/utils";
import AiSearchResults from "@/components/ai-search/AiSearchResults.vue";
import { FeatureType } from "@/enums";
export default defineComponent({
    name: "AiSearchResultsContainer",
    props: {
        portalId: { type: Number, required: true },
        searchText: { type: String, required: false, default: () => null },
        collectionId: { type: Number, required: false, default: () => null }
    },
    components: {
        AiFeedbackNotFound,
        SectionHeader,
        AiSearchResults
    },
    setup(props, { emit }) {
        const searchResults = ref<AiCacheSearchResultItem[] | undefined>(undefined);
        const minItemsCurrentPortalToShow: number = 3;
        const maxItemsCurrentPortalToShow: number = storeHelper.aiSearchStore.getPageSize();
        const shouldGenerateAnswer = (item: AiCacheSearchResultItem) => item.should_generate_answer == true && item.answer == null;
        const showMoreAmount = storeHelper.aiSearchStore.getPageSize();

        /**
         * The name of the portal to which the search results belong
         */
        const portalName = computed(() => {
            return storeHelper.portalStore.getPortal(props.portalId).name;
        });

        const aiAnswerEnabled = ref<boolean>(false);

        /**
         * Load the search results from the store
         */
        const loadSearchResultsAsync = async () => {
            //searchText is required, do not fetch any information if it is empty
            if (props.searchText == "") {
                searchResults.value = [];
                return;
            }

            // load results and ai answers
            emit("search-results-loaded", false);
            searchResults.value = null;
            searchResults.value = await getSearchResults();
            if (searchResults.value.length == 0) {
                emit("search-results-loaded", true);
                return;
            }
            cacheInitialItemsToShow();
            setAiAnswerGenerationForResults();
            searchResults.value
                ?.filter(shouldGenerateAnswer)
                .forEach(item => handleGenerateAiAnswer(item.content_item_id, item.in_current_portal ? "in_portal" : "outside_portal"));
            emit("search-results-loaded", true);
        };

        const loadAiAnswerFeatureSwitch = async () => {
            const cachedFeatures = storeHelper.FeaturesStore.features;
            if (cachedFeatures.length > 0) {
                aiAnswerEnabled.value = cachedFeatures.find(f => f.name == FeatureType.search_ai_answer)?.enabled;
                return;
            }

            aiAnswerEnabled.value = (await storeHelper.FeaturesStore.featureByType(FeatureType.search_ai_answer))?.enabled ?? false;
        };

        const setAiAnswerGenerationForResults = () => {
            //disable generating the answer when the feature switch is disabled
            if (aiAnswerEnabled.value != true) {
                searchResults.value.forEach(item => {
                    item.should_generate_answer = false;
                });
                return;
            }

            //disable generating answers when the collection is filtered
            if (props.collectionId != null) {
                searchResults.value?.forEach(item => {
                    item.should_generate_answer = false;
                });
            }

            // disable generating answers for outside portal items
            outsideCurrentPortalItems.value.forEach(item => {
                item.should_generate_answer = false;
            });
        };

        /**
         * Cache the initial number of items to show after the search results have been loaded
         */
        const cacheInitialItemsToShow = () => {
            if (props.collectionId != null) {
                storeHelper.aiSearchStore.setItemsToShow("collection", numberOfCollectionFilterItemsToShow.value);
                return;
            }
            storeHelper.aiSearchStore.setItemsToShow("in_portal", numberOfCurrentPortalItemsToShow.value);
            storeHelper.aiSearchStore.setItemsToShow("outside_portal", numberOfItemsOutsideCurrentPortalToShow.value);
        };

        /**
         * Send request to get the searchResults before the component has been mounted
         */
        onMounted(async () => {
            await Promise.all([loadSearchResultsAsync(), loadAiAnswerFeatureSwitch()]);
        });

        /**
         * if any props change, get the search results again
         */
        watch(
            () => props,
            () => loadSearchResultsAsync(),
            { deep: true }
        );

        const isSearchTextValid = (): boolean => {
            return /[\p{L}\d-]/u.test(props.searchText);
        };

        /**
         * Get search results from the store
         */
        const hasSearchRequestError = ref(false);
        const getSearchResults = async (): Promise<AiCacheSearchResultItem[]> => {
            if (!isSearchTextValid() && props.searchText != "") return [];
            hasSearchRequestError.value = false;

            //if collection is selected, get the search results for the collection
            if (props.collectionId != null) {
                const res = await storeHelper.aiSearchStore.loadSearchResultsAsync(
                    props.searchText,
                    "collection",
                    props.portalId,
                    props.collectionId
                );
                return res.results;
            }

            const [insideCurrentPortalItemsResult, outsideCurrentPortalItemsResult] = await Promise.all([
                storeHelper.aiSearchStore.loadSearchResultsAsync(props.searchText, "in_portal", props.portalId, null),
                storeHelper.aiSearchStore.loadSearchResultsAsync(props.searchText, "outside_portal", props.portalId, null)
            ]);

            //check if there is an error in the search results
            if (insideCurrentPortalItemsResult.status >= 500 || outsideCurrentPortalItemsResult.status >= 500) {
                hasSearchRequestError.value = true;
                return [];
            }

            return [...insideCurrentPortalItemsResult.results, ...outsideCurrentPortalItemsResult.results];
        };

        /**
         * Search results from within your portal
         */
        const currentPortalItems = computed(() => {
            return searchResults.value?.filter(item => item.in_current_portal == true);
        });

        /**
         * The initial number of items to show in the search results of your portal items
         * based on the score between the items in your portal and outside your portal
         */
        const numberOfCurrentPortalItemsToShow = computed(() => {
            //check if in cache
            var cachedItemsToShow = storeHelper.aiSearchStore.getItemsToShow("in_portal", props.searchText, props.portalId, props.collectionId);
            if (cachedItemsToShow != null) return cachedItemsToShow;

            //get the best score of the items outside your portal
            const bestOutsideCurrentPortalItemScore = outsideCurrentPortalItems.value.length > 0 ? outsideCurrentPortalItems.value[0].score : 0;
            const relevantCurrentPortalItems = currentPortalItems.value.filter(item => item.score >= bestOutsideCurrentPortalItemScore);

            // return at least 3 items
            const minAmount = Math.max(relevantCurrentPortalItems.length, minItemsCurrentPortalToShow);

            // return at most 10 items
            return Math.min(minAmount, maxItemsCurrentPortalToShow);
        });

        /**
         * Search results from outside your portal, but relevant to your search query
         */
        const outsideCurrentPortalItems = computed(() => {
            return searchResults.value?.filter(item => item.in_current_portal == false);
        });

        /**
         * The initial number of items to show in the search results of your portal items
         * based on the score between the items in your portal and outside your portal
         */
        const numberOfItemsOutsideCurrentPortalToShow = computed(() => {
            var cachedItemsToShow = storeHelper.aiSearchStore.getItemsToShow("outside_portal", props.searchText, props.portalId, props.collectionId);
            if (cachedItemsToShow != null) return cachedItemsToShow;

            // return at least 3 items
            const minAmount = Math.max(outsideCurrentPortalItems.value.length, minItemsCurrentPortalToShow);

            // return at most 10 items
            return Math.min(minAmount, maxItemsCurrentPortalToShow);
        });

        /**
         * The initial number of items to show in the search results of the collection filter
         */
        const numberOfCollectionFilterItemsToShow = computed(() => {
            var cachedItemsToShow = storeHelper.aiSearchStore.getItemsToShow("collection", props.searchText, props.portalId, props.collectionId);
            if (cachedItemsToShow != null) return cachedItemsToShow;

            return maxItemsCurrentPortalToShow;
        });

        /**
         * Handle when the user clicks on an item in the search results
         * @param item - The content item that was clicked
         */
        const router = getCurrentInstance().proxy.$router;
        const handleItemClick = (item: AiCacheSearchResultItem) => {
            const contentItem = {
                content_item_id: item.content_item_id,
                source_item_id: item.source_item_id,
                content_type: {
                    content_type_id: item.content_type_id
                },
                sub_type_field: {
                    value_id: item.content_sub_type_id
                }
            } as ContentItem;

            Utils.openContentItem(contentItem, router, props.portalId);
        };

        /**
         * Check if the AI answer should be generated for the selected item
         * @param item - The content item for which the AI answer should be generated
         */
        const isAllowedToGenerateAiAnswer = (item: AiCacheSearchResultItem): boolean => {
            if (aiAnswerEnabled.value != true) return false; //check if the feature is enabled
            if (item.answer != null) return false; //cannot generate answer if it is already generated
            if (item.content_type_id == 9 || item.content_type_id == 10) return true; //only generate answer for vilans protocols
            if (item.content_type_id == 1 && item.content_sub_type_id != "7") return true; //generate answer for documents that are not processes
            if (item.content_type_id == 6) return true; //generate answer for cards
            return false;
        };

        /**
         * Handle when the user wants to generate an AI answer for the selected item
         * @param item - The content item for which the AI answer should be generated
         */
        const handleGenerateAiAnswer = async (contentItemId: string, searchScope: "in_portal" | "outside_portal" | "collection") => {
            //check if the item exists in the search results
            const index = searchResults.value.findIndex(i => i.content_item_id == contentItemId);
            if (index < 0) return;

            //set the flag to show the answer for the selected item
            searchResults.value[index].should_generate_answer = true;
            //get the AI answer for the selected item
            if (props.collectionId != null) searchScope = "collection";
            const answer = await storeHelper.aiSearchStore.loadAiAnswerAsync(props.searchText, contentItemId, searchScope);
            if (answer == null) {
                searchResults.value[index].should_generate_answer = false;
                return;
            }
            //set the answer for the selected item
            searchResults.value[index].answer = answer;
        };

        /**
         * Handle when the user wants to show more items in the search results
         */
        const handleShowMoreAsync = async (
            searchScope: "in_portal" | "outside_portal" | "collection",
            currentItemsShown: number,
            showMoreAmount: number
        ) => {
            storeHelper.aiSearchStore.setItemsToShow(searchScope, currentItemsShown);
            // we buffer some results so that the results are always loaded
            // if we are close to the end of the buffer, we load more
            const currentResultsDict = {
                in_portal: currentPortalItems.value.length,
                outside_portal: outsideCurrentPortalItems.value.length,
                collection: searchResults.value.length
            };
            const amountOfItemsLoaded = currentResultsDict[searchScope];
            const shouldBuffer = amountOfItemsLoaded - currentItemsShown < showMoreAmount;
            if (shouldBuffer) {
                const newItemsResult = await storeHelper.aiSearchStore.loadMoreAsync(searchScope);
                //if the status is 404, it means that all items are loaded
                if (newItemsResult.status == 404) {
                    storeHelper.aiSearchStore.clearCache(searchScope);
                    loadSearchResultsAsync();
                    return;
                }
                searchResults.value = [...searchResults.value, ...newItemsResult.results];
            }
        };

        /**
         * Check if all items are loaded
         */
        const areAllItemsLoaded = (scope: "in_portal" | "outside_portal" | "collection") => {
            return storeHelper.aiSearchStore.areAllItemsLoaded(scope);
        };

        return {
            searchResults,
            currentPortalItems,
            outsideCurrentPortalItems,
            numberOfCurrentPortalItemsToShow,
            numberOfItemsOutsideCurrentPortalToShow,
            numberOfCollectionFilterItemsToShow,
            maxItemsCurrentPortalToShow,
            portalName,
            showMoreAmount,
            aiAnswerEnabled,
            hasSearchRequestError,
            handleItemClick,
            handleGenerateAiAnswer,
            handleShowMoreAsync,
            areAllItemsLoaded,
            isAllowedToGenerateAiAnswer,
            props
        };
    }
});
